import { Link } from 'wouter';

import './footer.css';
import type { ResourceLink, SocialLink } from './footer.types';
import { resourceLinks, socialLinks, quickLinks } from './footer.helpers';

export default function Footer() {
  return (
    <footer className="bg-[#0a0a0b] border-t border-white/10 relative z-10">
      <div className="max-w-screen-xl mx-auto px-4 pt-10 md:pt-16 pb-6 md:pb-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-8">
          {/* Logo and Company Info */}
          <div className="col-span-2 md:col-span-1 lg:col-span-1">
            <img
              src="/assets/logo.png"
              alt="7Sigma"
              style={{ width: '150px', height: '50px' }}
              className="object-contain mb-6"
            />
            <p className="text-gray-100 text-sm mb-2">
              Senior architecture and technical leadership.
            </p>
            <p className="text-gray-300 text-xs mb-4">Leader · Builder · Operator</p>
            <div className="text-gray-300 text-[12px] mb-6 space-y-1">
              <p>7Sigma Partners LLC</p>
            </div>
            <div className="social-links">
              {socialLinks.map((social: SocialLink) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    className="footer-link group"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Visit our ${social.label} page`}
                  >
                    <IconComponent />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links Column */}
          <div className="md:col-span-1 lg:col-span-1">
            <h3 className="footer-heading">Quick Links</h3>
            <ul className="footer-links">
              {quickLinks.map((link: ResourceLink) => (
                <li key={link.href}>
                  <Link href={link.href}>
                    <span className="footer-link cursor-pointer">{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div className="md:col-span-1 lg:col-span-1">
            <h3 className="footer-heading">Contact</h3>
            <div className="space-y-3">
              <div>
                <p className="text-gray-100 text-sm">7Sigma Partners LLC</p>
                <p className="text-gray-300 text-xs">255 S Orange Avenue</p>
                <p className="text-gray-300 text-xs">Suite 104</p>
                <p className="text-gray-300 text-xs">Orlando, FL 32801</p>
              </div>
              <p className="text-gray-300 text-sm">
                <a href="mailto:discovery@7sigma.io" className="footer-link">
                  discovery@7sigma.io
                </a>
              </p>
              <Link href="/contact" className="group">
                <span className="footer-contact-link cursor-pointer inline-flex items-center">
                  Book a Meeting
                  <span className="ml-2 group-hover:translate-x-1 transition-transform duration-200 inline-block">
                    →
                  </span>
                </span>
              </Link>
            </div>
          </div>

          {/* Team Column */}
          <div className="md:col-span-1 lg:col-span-1">
            <h3 className="footer-heading">Team</h3>
            <ul className="footer-links">
              <li>
                <Link href="/contact/partner-network">
                  <span className="footer-link cursor-pointer">Apply to the Network</span>
                </Link>
              </li>
            </ul>

            <h3 className="footer-heading mt-6">Resources</h3>
            <ul className="footer-links">
              {resourceLinks.map((link: ResourceLink) => (
                <li key={link.href}>
                  {link.target === '_blank' ? (
                    <a href={link.href} target="_blank" rel="noopener noreferrer">
                      <span className="footer-link cursor-pointer">{link.label}</span>
                    </a>
                  ) : (
                    <Link href={link.href}>
                      <span className="footer-link cursor-pointer">{link.label}</span>
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services Column. These are Worker-rendered landing pages (not SPA routes),
              so they use plain <a> for full-page navigation to the Worker. */}
          <div className="md:col-span-1 lg:col-span-1">
            <h3 className="footer-heading">Services</h3>
            <ul className="footer-links">
              <li>
                <a href="/fractional-cto">
                  <span className="footer-link cursor-pointer">Fractional CTO</span>
                </a>
              </li>
              <li>
                <a href="/vibe-coded-app-rescue">
                  <span className="footer-link cursor-pointer">Vibe Coded App Rescue</span>
                </a>
              </li>
              <li>
                <a href="/technical-mercenary">
                  <span className="footer-link cursor-pointer">Technical Mercenary</span>
                </a>
              </li>
              <li>
                <a href="/ai-consulting">
                  <span className="footer-link cursor-pointer">AI Consulting</span>
                </a>
              </li>
              <li>
                <a href="/custom-software-development">
                  <span className="footer-link cursor-pointer">Custom Software</span>
                </a>
              </li>
              <li>
                <a href="/digital-transformation">
                  <span className="footer-link cursor-pointer">Digital Transformation</span>
                </a>
              </li>
            </ul>

            <h3 className="footer-heading mt-6">Service Area</h3>
            <ul className="footer-links">
              <li>
                <a href="/orlando">
                  <span className="footer-link cursor-pointer">Orlando</span>
                </a>
              </li>
              <li>
                <a href="/central-florida">
                  <span className="footer-link cursor-pointer">Central Florida</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="footer-bottom text-sm">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <p className="text-sm text-gray-300 mb-4 sm:mb-0">
              © 2026 7Sigma Partners LLC. All Rights Reserved.
            </p>
            <div className="flex space-x-6">
              <Link href="/privacy">
                <span className="footer-bottom text-sm-link cursor-pointer">Privacy Policy</span>
              </Link>
              <span className="text-gray-400"> | </span>
              <Link href="/terms">
                <span className="footer-bottom text-sm-link cursor-pointer">Terms of Service</span>
              </Link>
            </div>
          </div>
          <div className="text-center mt-6 pt-6 border-t border-gray-800 pb-safe">
            <div className="flex items-center justify-center gap-2">
              <span className="text-sm text-gray-300 font-semibold">Built by</span>
              <img src="/assets/favicon.png" alt="7Sigma" className="h-4 w-auto" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
